import React, { useEffect, useState, SyntheticEvent } from 'react';
import { useAuth } from '@saltus/auth';
import { isEmpty, orderBy } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SwipeableDrawer,
  Box,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  Tooltip,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Minimize';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { withStyles } from '@material-ui/core/styles';
import getSlug from '../../services/getSlug';
import handleSignOut from '../../services/handleSignOut';
import calcPlanOwnerTotals from '../../services/util/calcPlanOwnerTotals';
import {
  LogoIcon,
  ListItemIcon as _ListItemIcon,
} from '../../theme/styled-components';
import styles from './styles';

// withTheme wrapper strips className from the styled-component type, but it works at runtime
const ListItemIcon = _ListItemIcon as React.ComponentType<{
  className?: string;
}>;

interface PlanOwnerItem {
  Label: string;
  IsAboveTheLine: boolean;
  Plans: { OnOffPlatform: string; PlanValueInAcctCcy: number | string }[];
}

interface NavigationDrawerProps {
  classes: Record<string, string>;
  dashboard: { planOwners?: PlanOwnerItem[] };
  dashboarderror: Record<string, unknown>;
  accounttype: { isFamily?: boolean };
  contact: Record<string, unknown>;
  contactStatus: string;
  handleOpenNavigationDrawer: (
    e: SyntheticEvent<unknown> | null,
    isOpen: boolean,
  ) => void;
  navigationdraweropen: boolean;
}

function NavigationDrawer({
  classes = {},
  dashboard = {},
  dashboarderror = {},
  accounttype = {},
  contact = {},
  contactStatus = '',
  handleOpenNavigationDrawer = () => {},
  navigationdraweropen = false,
}: NavigationDrawerProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [isAzureAD, setIsAzureAD] = useState(false);

  const handleChange =
    (panel: boolean) =>
    (
      _event: React.ChangeEvent<Record<string, unknown>>,
      newExpanded: boolean,
    ) => {
      setExpanded(newExpanded ? panel : false);
    };

  const navItemTestId = (label: string) =>
    `navigation-item-${label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')}`;

  const onClose = (e: SyntheticEvent<unknown>) => {
    handleOpenNavigationDrawer(e, false);
  };

  const orderedPlanOwners = orderBy(
    dashboard.planOwners,
    [
      (a: PlanOwnerItem) => a.IsAboveTheLine,
      (a: PlanOwnerItem) => calcPlanOwnerTotals(a).TotalPortfolioWealth,
      (a: PlanOwnerItem) => a.Label,
    ],
    ['desc', 'desc', 'asc'],
  );

  useEffect(() => {
    if (user) {
      setIsAzureAD(() => {
        if (user.attributes.identities) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const identities: { providerType?: string; providerName?: string }[] =
            JSON.parse(user.attributes.identities);
          const identity = identities.find(
            (item) =>
              item?.providerType === 'SAML' &&
              item?.providerName?.endsWith('AD'),
          );

          return !!identity;
        }

        if (user) {
          const azureAdGroup = user.groups?.find((group: string) =>
            group.endsWith('AD'),
          );

          return !!azureAdGroup;
        }

        return false;
      });
    }
  }, [user]);

  return (
    <SwipeableDrawer
      classes={{
        paper: classes.drawerPaper,
      }}
      disableSwipeToOpen={false}
      hysteresis={0.25}
      minFlingVelocity={300}
      swipeAreaWidth={0}
      anchor="right"
      open={navigationdraweropen}
      onClose={(e) => handleOpenNavigationDrawer(e, false)}
      onOpen={() => {}}
    >
      <Box className={classes.drawerHeader}>
        {import.meta.env.VITE_APP_ENV &&
          import.meta.env.VITE_APP_ENV !== 'production' && (
            <span className={classes.envName}>
              {import.meta.env.VITE_APP_ENV}
            </span>
          )}

        <LogoIcon />
      </Box>
      <List className={classes.navigationMenu} data-testid="navigation-drawer">
        {isEmpty(dashboarderror) && contactStatus !== 'Prospect' ? (
          <ListItem
            onClick={(e) => {
              navigate('/dashboard');
              onClose(e);
            }}
            className={
              location.pathname === '/dashboard'
                ? 'navigationListItem active'
                : 'navigationListItem'
            }
            data-testid={navItemTestId('Dashboard')}
            button
          >
            <ListItemText primary="Dashboard" />
            <ListItemIcon
              className={location.pathname === '/dashboard' ? 'left-blue' : ''}
            />
          </ListItem>
        ) : (
          <Tooltip
            title="Dashboard not currently available"
            classes={{
              tooltip: classes.customTooltip,
            }}
          >
            <ListItem
              className="navigationListItem disabled"
              data-testid={navItemTestId('Dashboard')}
            >
              <ListItemText primary="Dashboard" />
              <LockOutlinedIcon
                className={classes.lockedIcon}
                data-testid="navigation-item-dashboard-lock-icon"
              />
            </ListItem>
          </Tooltip>
        )}

        {accounttype &&
          accounttype.isFamily &&
          orderedPlanOwners.length > 0 &&
          (isEmpty(dashboarderror) && contactStatus !== 'Prospect' ? (
            <Accordion
              expanded={expanded === true}
              onChange={handleChange(!expanded)}
              className={classes.navigationAccordion}
              data-testid={navItemTestId('Accounts')}
            >
              <AccordionSummary
                expandIcon={expanded === true ? <RemoveIcon /> : <AddIcon />}
                aria-controls="navigation-dropdown"
                className={classes.navigationAccordionItem}
              >
                <Typography className="text" variant="body1">
                  Accounts
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List className={classes.navigationMenu}>
                  {orderedPlanOwners.map((name, index) => (
                    <ListItem
                      onClick={(e) => {
                        navigate(
                          `/dashboard/${getSlug(name.Label.toLowerCase())}`,
                        );

                        onClose(e);
                      }}
                      key={index}
                      className={classes.navigationDropDownListItem}
                      data-testid={`navigation-account-item-${index}`}
                      button
                    >
                      <ListItemIcon
                        className={classes.navigationDropDownListItemIcon}
                      />
                      <ListItemText
                        className={classes.navigationDropDownListItemText}
                        primary={name.Label}
                      />
                    </ListItem>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ) : (
            <Tooltip
              title="Dashboard not currently available"
              classes={{
                tooltip: classes.customTooltip,
              }}
            >
              <ListItem
                className="navigationListItem disabled"
                data-testid={navItemTestId('Accounts')}
              >
                <ListItemText primary="Account" />
                <LockOutlinedIcon
                  className={classes.lockedIcon}
                  data-testid="navigation-item-accounts-lock-icon"
                />
              </ListItem>
            </Tooltip>
          ))}

        {!isEmpty(contact) && (
          <ListItem
            onClick={(e) => {
              navigate('/actions');
              onClose(e);
            }}
            className={
              location.pathname === '/actions'
                ? 'navigationListItem active'
                : 'navigationListItem'
            }
            data-testid={navItemTestId('Actions')}
            button
          >
            <ListItemText primary="Actions" />
            <ListItemIcon
              className={location.pathname === '/actions' ? 'left-blue' : ''}
            />
          </ListItem>
        )}

        <ListItem
          onClick={(e) => {
            navigate('/documents');
            onClose(e);
          }}
          className={
            location.pathname === '/documents'
              ? 'navigationListItem active'
              : 'navigationListItem'
          }
          data-testid={navItemTestId('Documents')}
          button
        >
          <ListItemText primary="Documents" />
          <ListItemIcon
            className={location.pathname === '/documents' ? 'left-blue' : ''}
          />
        </ListItem>

        {!isEmpty(contact) && (
          <ListItem
            onClick={(e) => {
              navigate('/personal-details');
              onClose(e);
            }}
            className={
              location.pathname === '/personal-details'
                ? 'navigationListItem active'
                : 'navigationListItem'
            }
            data-testid={navItemTestId('Personal details')}
            button
          >
            <ListItemText primary="Personal details" />
            <ListItemIcon
              className={
                location.pathname === '/personal-details' ? 'left-blue' : ''
              }
            />
          </ListItem>
        )}
        {!isAzureAD && (
          <ListItem
            onClick={(e) => {
              navigate('/security');
              onClose(e);
            }}
            className={
              location.pathname === '/security'
                ? 'navigationListItem active'
                : 'navigationListItem'
            }
            data-testid={navItemTestId('Security')}
            button
          >
            <ListItemText primary="Security" />
            <ListItemIcon
              className={location.pathname === '/security' ? 'left-blue' : ''}
            />
          </ListItem>
        )}

        {!isEmpty(contact) && (
          <ListItem
            onClick={(e) => {
              navigate('/contact-us');
              onClose(e);
            }}
            className={
              location.pathname === '/contact-us'
                ? 'navigationListItem active'
                : 'navigationListItem'
            }
            data-testid={navItemTestId('Contact us')}
            button
          >
            <ListItemText primary="Contact us" />
            <ListItemIcon
              className={location.pathname === '/contact-us' ? 'left-blue' : ''}
            />
          </ListItem>
        )}

        <ListItem
          onClick={() => {
            window.open(import.meta.env.VITE_APP_PRIVACY_POLICY_URL);
          }}
          className="navigationListItem"
          data-testid={navItemTestId('Privacy policy')}
          button
        >
          <ListItemText primary="Privacy policy" />
          <ListItemIcon />
        </ListItem>

        <ListItem className={classes.navigationListItemSignOut}>
          <ListItemText>
            <Button
              onClick={(e) => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                handleSignOut(e);
              }}
              data-testid={navItemTestId('Sign out')}
              className={classes.signOutBtn}
              variant="outlined"
            >
              Sign out
            </Button>
          </ListItemText>
        </ListItem>
      </List>
    </SwipeableDrawer>
  );
}

export default withStyles(styles)(NavigationDrawer);
